import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ShipmentDetail, ShipmentStatus } from '../../types/shipment.types';
import { Campaign } from '../../types/campaign.types';
import NewOrganizationModal from '../Donation/NewOrganizationModal';
import { FaInfoCircle } from 'react-icons/fa';
import { Button } from '../../components/Button';

interface NewShipmentModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (shipment: Partial<ShipmentDetail>) => void;
  campaigns: Campaign[];
  organizations: string[];
  loadingCampaigns: boolean;
  loadingOrganizations: boolean;
}

const NewShipmentModal: React.FC<NewShipmentModalProps> = ({
  open,
  onClose,
  onConfirm,
  campaigns,
  organizations,
  loadingCampaigns,
  loadingOrganizations
}) => {
  // State for form data
  const [formData, setFormData] = useState({
    name: '',
    supplierId: '',
    label: '',
    campaignId: 0,
    orderedBoxes: 1,
    organization: '',
    address: '',
    logisticsDetails: '',
    notifyDonors: true,
    latitude: 0,
    longitude: 0,
    item: 'UK Homeless (Porch)', // Default item, would normally come from API
    languageCode: 'en', // Default language code
  });

  // State for validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // State for loading status
  const [loading, setLoading] = useState(false);

  // State for organization modal
  const [showOrgModal, setShowOrgModal] = useState(false);

  // Reset form when modal is opened/closed
  useEffect(() => {
    if (open) {
      setFormData({
        name: '',
        supplierId: '',
        label: '',
        campaignId: 0,
        orderedBoxes: 1,
        organization: '',
        address: '',
        logisticsDetails: '',
        notifyDonors: true,
        latitude: 0,
        longitude: 0,
        item: 'UK Homeless (Porch)',
        languageCode: 'en',
      });
      setErrors({});
    }
  }, [open]);

  // Don't render anything if modal is closed
  if (!open) return null;

  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox'
        ? (e.target as HTMLInputElement).checked
        : (type === 'number' ? Number(value) : value)
    }));

    // Clear error message for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handle organization creation
  const handleNewOrg = (org: { name: string; url: string }) => {
    // Add to the list and select it
    setFormData(prev => ({
      ...prev,
      organization: org.name
    }));
  };

  // Form validation
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.supplierId.trim()) newErrors.supplierId = 'ID is required';
    if (!formData.campaignId) newErrors.campaignId = 'Campaign is required';
    if (!formData.organization.trim()) newErrors.organization = 'Organization is required';
    if (formData.orderedBoxes < 1) newErrors.orderedBoxes = 'Quantity must be at least 1';
    if (!formData.address.trim()) newErrors.address = 'Location is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = () => {
    if (!validateForm()) return;

    setLoading(true);

    // Prepare shipment payload
    const shipment: Partial<ShipmentDetail> = {
      ...formData,
      status: ShipmentStatus.CREATED,
      // Include the organization with the correct field name for API
      organization: formData.organization,
    };

    try {
      // Pass to parent component for API call
      onConfirm(shipment);
    } catch (error) {
      console.error('Error creating shipment:', error);
    } finally {
      setLoading(false);
    }
  };

  // Geocoding function for location search
  const handleGeocoding = (address: string) => {
    if (!address.trim()) {
      setErrors(prev => ({
        ...prev,
        address: 'Address is required for geocoding'
      }));
      return;
    }

    // In a real implementation, this would call a geocoding service API
    // For now, we'll set dummy coordinates

    // Simulate successful geocoding with fixed values for demo
    const latitude = 51.5074;
    const longitude = -0.1278;

    setFormData(prev => ({
      ...prev,
      latitude,
      longitude
    }));

    // Alert user that geocoding was "successful"
    alert(`Location found! Coordinates: ${latitude}, ${longitude}`);
  };

  // Define item options for the dropdown
  const itemOptions = [
    'UK Homeless (Porch)',
    'Food Bank',
    'Community Center',
    'School Program'
  ];

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-white/50 dark:border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center border-b dark:border-white/10 px-8 py-6">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Create New Shipment</h2>
          <Button
            variant="tertiary"
            size="sm"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </Button>
        </div>

        {/* Form Body */}
        <div className="px-8 py-6">
          {/* Basic Info Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-gray-700 dark:text-gray-300 mb-1">
                Shipment ID
              </label>
              <input
                name="supplierId"
                className={`border rounded px-3 py-2 w-full dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 ${errors.supplierId ? 'border-red-500' : ''}`}
                value={formData.supplierId}
                onChange={handleChange}
                placeholder="e.g., UKHomelessPorch"
              />
              {errors.supplierId && <p className="text-red-500 text-sm mt-1">{errors.supplierId}</p>}
            </div>

            <div>
              <label className="flex items-center text-gray-700 dark:text-gray-300 mb-1">
                Name
                <span className="ml-1 text-gray-400 cursor-help" title="Display name for the shipment">
                  <FaInfoCircle />
                </span>
              </label>
              <input
                name="name"
                className={`border rounded px-3 py-2 w-full dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 ${errors.name ? 'border-red-500' : ''}`}
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., UK Homeless Porch 9Jan2025"
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>
          </div>

          {/* Label Field */}
          <div className="mb-6">
            <label className="block text-gray-700 dark:text-gray-300 mb-1">
              Label (optional)
            </label>
            <input
              name="label"
              className="border rounded px-3 py-2 w-full dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
              value={formData.label}
              onChange={handleChange}
              placeholder="Additional label information"
            />
          </div>

          {/* Campaign and Item */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-gray-700 dark:text-gray-300 mb-1">Select Campaign</label>
              <select
                name="campaignId"
                className={`border rounded px-3 py-2 w-full dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 ${errors.campaignId ? 'border-red-500' : ''}`}
                value={formData.campaignId}
                onChange={handleChange}
                disabled={loadingCampaigns}
              >
                <option value={0}>Select a campaign</option>
                {campaigns.map(campaign => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </option>
                ))}
              </select>
              {errors.campaignId && <p className="text-red-500 text-sm mt-1">{errors.campaignId}</p>}
              {loadingCampaigns && <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Loading campaigns...</p>}
            </div>

            <div>
              <label className="block text-gray-700 dark:text-gray-300 mb-1">Item</label>
              <select
                name="item"
                className="border rounded px-3 py-2 w-full dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
                value={formData.item}
                onChange={handleChange}
              >
                {itemOptions.map(item => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Ordered quantity & Organization */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-gray-700 dark:text-gray-300 mb-1">Ordered Quantity</label>
              <input
                type="number"
                name="orderedBoxes"
                className={`border rounded px-3 py-2 w-full dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 ${errors.orderedBoxes ? 'border-red-500' : ''}`}
                value={formData.orderedBoxes}
                onChange={handleChange}
                min={1}
              />
              {errors.orderedBoxes && <p className="text-red-500 text-sm mt-1">{errors.orderedBoxes}</p>}
            </div>

            <div>
              <label className="block text-gray-700 dark:text-gray-300 mb-1">Organization</label>
              <div className="flex gap-2">
                <select
                  name="organization"
                  className={`border rounded px-3 py-2 flex-1 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 ${errors.organization ? 'border-red-500' : ''}`}
                  value={formData.organization}
                  onChange={handleChange}
                  disabled={loadingOrganizations}
                >
                  <option value="">Select an organization</option>
                  {organizations.map(org => (
                    <option key={org} value={org}>
                      {org}
                    </option>
                  ))}
                </select>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowOrgModal(true)}
                >
                  Add New
                </Button>
              </div>
              {errors.organization && <p className="text-red-500 text-sm mt-1">{errors.organization}</p>}
              {loadingOrganizations && <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Loading organizations...</p>}
            </div>
          </div>

          {/* Location */}
          <div className="mb-6">
            <label className="block text-gray-700 dark:text-gray-300 mb-1">Location</label>
            <div className="flex gap-2">
              <input
                name="address"
                className={`border rounded px-3 py-2 w-full dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 ${errors.address ? 'border-red-500' : ''}`}
                value={formData.address}
                onChange={handleChange}
                placeholder="Enter address"
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleGeocoding(formData.address)}
              >
                Search
              </Button>
            </div>
            {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}

            {/* Show coordinates if they've been set */}
            {(formData.latitude !== 0 && formData.longitude !== 0) && (
              <div className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Coordinates: {formData.latitude}, {formData.longitude}
              </div>
            )}

            {/* In a real implementation, we would show a map preview here */}
          </div>

          {/* Logistics details */}
          <div className="mb-6">
            <label className="block text-gray-700 dark:text-gray-300 mb-1">Logistics Details</label>
            <textarea
              name="logisticsDetails"
              className="border rounded px-3 py-2 w-full h-24 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
              value={formData.logisticsDetails}
              onChange={handleChange}
              placeholder="Enter tracking link or logistics information"
            ></textarea>
          </div>

          {/* Notify donors */}
          <div className="mb-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="notifyDonors"
                checked={formData.notifyDonors}
                onChange={handleChange}
                className="h-4 w-4 text-primary-100 border-gray-300 dark:border-white/10 dark:bg-slate-800 rounded mr-2"
              />
              <span className="dark:text-gray-300">Notify donors</span>
            </label>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              If checked, donors will be notified when this shipment is created.
            </p>
          </div>
        </div>

        {/* Footer with actions */}
        <div className="flex justify-end gap-2 px-8 py-6 border-t dark:border-white/10">
          <Button
            variant="secondary"
            size="md"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Creating..." : "Confirm"}
          </Button>
        </div>
      </div>

      {/* Organization Modal */}
      <NewOrganizationModal
        open={showOrgModal}
        onClose={() => setShowOrgModal(false)}
        onConfirm={handleNewOrg}
      />
    </div>
  ,
  document.body
);
};

export default NewShipmentModal;
